/**
 * BI P3：租客三個 campaign 的問題字典（`docs/behavior-insights/feedback-catalog.md`
 * §1.2–§1.4；規格見 `docs/behavior-insights/implementation-p3.md` §1.1、§2）。
 *
 * 後端 `answer_code` 一律 ASCII，不回中文（catalog §0.4）——文案與選項顯示順序
 * 由這份字典決定。`GET /api/feedback/next` 只回 `campaign` + `question_version`，
 * 不回 `question_key`，所以字典要能從 campaign 反查「這個 campaign 該問哪幾題」。
 *
 * 字典結構刻意用 `Map<questionKey, Map<version, FeedbackQuestion>>` 而非攤平的單一
 * 版本：改題流程（catalog §0.3）要求同一 question_key 的舊版與新版文案能並存一段
 * 時間，直到舊邀請全部到期——攤平會讓升版當天舊邀請顯示壞掉。
 */

export interface FeedbackOption {
    code: string
    label: string
}

export interface FeedbackQuestion {
    questionKey: string
    version: number
    text: string
    options: FeedbackOption[]
    /** 只有帶 `other` 選項的題目才需要，見 §0.1 自由文字不是 answer_code。 */
    optionalTextAllowed?: boolean
}

type QuestionVersions = Map<number, FeedbackQuestion>

interface CampaignDef {
    /** 依序呈現的 question_key 列表（本輪三個 campaign 都只有一題）。 */
    questionKeys: string[]
    questions: Map<string, QuestionVersions>
}

function toVersions(questions: FeedbackQuestion[]): QuestionVersions {
    const versions: QuestionVersions = new Map()
    for (const q of questions) {
        versions.set(q.version, q)
    }
    return versions
}

function defineCampaign(questionKeys: string[], questions: FeedbackQuestion[]): CampaignDef {
    const byKey = new Map<string, QuestionVersions>()
    for (const key of questionKeys) {
        byKey.set(key, toVersions(questions.filter((q) => q.questionKey === key)))
    }
    return {questionKeys, questions: byKey}
}

const CATALOG: ReadonlyMap<string, CampaignDef> = new Map([
    // feedback-catalog.md §1.2：租客詢問後 3 天，回覆率缺口。
    ['renter-reply-check', defineCampaign(['got_reply'], [
        {
            questionKey: 'got_reply',
            version: 1,
            text: '有得到回覆嗎？',
            options: [
                {code: 'replied_onsite', label: '站內已回'},
                {code: 'replied_offsite', label: 'LINE 或電話已回'},
                {code: 'no_reply', label: '未回'},
                {code: 'unsure', label: '不確定'},
            ],
        },
    ])],

    // feedback-catalog.md §1.3：租客詢問後 7 天，七日結果缺口。
    ['renter-search-outcome', defineCampaign(['final_outcome'], [
        {
            questionKey: 'final_outcome',
            version: 1,
            text: '最後找到場地嗎？',
            options: [
                {code: 'deal_this_merchant_onsite', label: '此商家站內成交'},
                {code: 'deal_this_merchant_offsite', label: '此商家站外成交'},
                {code: 'deal_elsewhere', label: '別家'},
                {code: 'still_searching', label: '仍在找'},
                {code: 'gave_up', label: '不租了'},
            ],
        },
    ])],

    // feedback-catalog.md §1.4：操作失敗或主動回報，雙方共用（此檔僅租客端使用）。
    ['blocker-report', defineCampaign(['blocker_kind'], [
        {
            questionKey: 'blocker_kind',
            version: 1,
            text: '這一步卡在哪？',
            options: [
                {code: 'dont_understand', label: '不懂'},
                {code: 'cant_find', label: '找不到'},
                {code: 'data_not_ready', label: '資料未備齊'},
                {code: 'too_slow', label: '太慢'},
                {code: 'error', label: '錯誤'},
                {code: 'no_need', label: '無需求'},
                {code: 'other', label: '其他'},
            ],
            optionalTextAllowed: true,
        },
    ])],
])

/**
 * 查一題。`version` 省略時（僅 self-report 主動入口這麼用，沒有後端版本可比對）
 * 抓字典裡最新的一版。`version` 有指定但查無該版本時回 `null`，不退而求其次借用
 * 別版本——catalog §0.3 明文禁止版本間靜默合併，字面文案同理不該混用，寧可這批
 * 邀請暫時沒人回答，也不要顯示語意可能已經變了的舊/新文案。查無此 campaign 或此
 * question_key 同樣回 `null`，呼叫端安靜跳過（不拋錯）。
 */
export function getFeedbackQuestion(campaign: string, questionKey: string, version?: number): FeedbackQuestion | null {
    const def = CATALOG.get(campaign)
    if (!def) return null

    const versions = def.questions.get(questionKey)
    if (!versions || versions.size === 0) return null

    if (version !== undefined) {
        return versions.get(version) ?? null
    }

    const latestVersion = Math.max(...versions.keys())
    return versions.get(latestVersion) ?? null
}

/**
 * 依 campaign 解出要依序渲染的所有題目（本輪三個 campaign 都只有一題）。
 * 不認得的 campaign 回 `null`——`FeedbackPrompt.vue` 據此判斷「不顯示任何東西」，
 * 這是安全閥：後端新增 campaign 不會讓舊版前端炸掉。
 */
export function getCampaignQuestions(campaign: string, version: number): FeedbackQuestion[] | null {
    const def = CATALOG.get(campaign)
    if (!def) return null

    const questions = def.questionKeys
        .map((key) => getFeedbackQuestion(campaign, key, version))
        .filter((q): q is FeedbackQuestion => q !== null)

    return questions.length > 0 ? questions : null
}
